import { apiClient } from '@/lib/api-client';
import {
  Projeto,
  TipoProjeto,
  ProjetoFase,
  ProjetoInvestimento,
  ProjetoDashboard,
  ProjetoStatus,
  ApiResponse,
} from '@/types';

export const projetoService = {
  async getAll(status?: ProjetoStatus, empresa_id?: string): Promise<ApiResponse<Projeto[]>> {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (empresa_id) params.set('empresa_id', empresa_id);
      const url = params.toString() ? `/projetos?${params.toString()}` : '/projetos';
      const response = await apiClient.get<ApiResponse<Projeto[]>>(url);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar projetos',
        data: [],
      };
    }
  },

  async getById(id: string): Promise<ApiResponse<Projeto>> {
    try {
      const response = await apiClient.get<ApiResponse<Projeto>>(`/projetos/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar projeto',
        data: {} as Projeto,
      };
    }
  },

  async create(
    data: Partial<Projeto> & { investimentos?: Omit<ProjetoInvestimento, 'id' | 'projeto_id'>[] }
  ): Promise<ApiResponse<Projeto>> {
    try {
      const response = await apiClient.post<ApiResponse<Projeto>>('/projetos', data);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar projeto',
        data: {} as Projeto,
      };
    }
  },

  async update(
    id: string,
    data: Partial<Projeto> & { investimentos?: Omit<ProjetoInvestimento, 'id' | 'projeto_id'>[] }
  ): Promise<ApiResponse<Projeto>> {
    try {
      const response = await apiClient.put<ApiResponse<Projeto>>(`/projetos/${id}`, data);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar projeto',
        data: {} as Projeto,
      };
    }
  },

  async delete(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`/projetos/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir projeto',
        data: undefined,
      };
    }
  },

  async updateStatus(id: string, status: ProjetoStatus): Promise<ApiResponse<Projeto>> {
    try {
      const response = await apiClient.patch<ApiResponse<Projeto>>(`/projetos/${id}/status`, { status });
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar status',
        data: {} as Projeto,
      };
    }
  },

  async getDashboard(empresa_id?: string): Promise<ApiResponse<ProjetoDashboard>> {
    try {
      const url = empresa_id ? `/projetos/dashboard?empresa_id=${empresa_id}` : '/projetos/dashboard';
      const response = await apiClient.get<ApiResponse<ProjetoDashboard>>(url);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar dashboard',
        data: {
          totais: { total: 0, em_edicao: 0, ag_aprovacao: 0, em_andamento: 0, ag_validacao: 0, finalizado: 0, reprovado: 0 },
          por_tipo: [],
          por_mes: [],
        },
      };
    }
  },

  async getFases(projetoId: string): Promise<ApiResponse<ProjetoFase[]>> {
    try {
      const response = await apiClient.get<ApiResponse<ProjetoFase[]>>(`/projetos/${projetoId}/fases`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar fases',
        data: [],
      };
    }
  },

  async createFase(projetoId: string, data: Partial<ProjetoFase>): Promise<ApiResponse<ProjetoFase>> {
    try {
      const response = await apiClient.post<ApiResponse<ProjetoFase>>(`/projetos/${projetoId}/fases`, data);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar fase',
        data: {} as ProjetoFase,
      };
    }
  },

  async updateFase(projetoId: string, faseId: string, data: Partial<ProjetoFase>): Promise<ApiResponse<ProjetoFase>> {
    try {
      const response = await apiClient.put<ApiResponse<ProjetoFase>>(`/projetos/${projetoId}/fases/${faseId}`, data);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar fase',
        data: {} as ProjetoFase,
      };
    }
  },

  async deleteFase(projetoId: string, faseId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`/projetos/${projetoId}/fases/${faseId}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir fase',
        data: undefined,
      };
    }
  },
};

export const tipoProjetoService = {
  async getAll(empresa_id?: string): Promise<ApiResponse<TipoProjeto[]>> {
    try {
      const url = empresa_id ? `/tipos-projeto?empresa_id=${empresa_id}` : '/tipos-projeto';
      const response = await apiClient.get<ApiResponse<TipoProjeto[]>>(url);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar tipos de projeto',
        data: [],
      };
    }
  },

  async create(data: { nome: string; descricao?: string; empresa_id?: string }): Promise<ApiResponse<TipoProjeto>> {
    try {
      const response = await apiClient.post<ApiResponse<TipoProjeto>>('/tipos-projeto', data);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar tipo de projeto',
        data: {} as TipoProjeto,
      };
    }
  },

  async update(id: string, data: { nome?: string; descricao?: string; ativo?: boolean }): Promise<ApiResponse<TipoProjeto>> {
    try {
      const response = await apiClient.put<ApiResponse<TipoProjeto>>(`/tipos-projeto/${id}`, data);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao atualizar tipo de projeto',
        data: {} as TipoProjeto,
      };
    }
  },

  async delete(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`/tipos-projeto/${id}`);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao excluir tipo de projeto',
        data: undefined,
      };
    }
  },
};
